import { useEffect, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Accordion, Button } from "react-bootstrap";
import moment from "moment";

import PlaylistModal from "@/components/Layout/PlaylistModal/PlaylistModal";
import fetchAPI from "@/components/utils/fetch-API";
import { useUser } from "@/components/contexts/user";
import Loader from "../common/Loader";
import { useNowPlaying } from "../contexts/now-playing";
import styles from "@/styles/Playlist.module.css"

function Playlist({ playlist, id }) {
  const user = useUser();

  return (
    <Accordion.Item eventKey={playlist.id}>
      <Accordion.Header>
        {playlist.title && `${playlist.title} — `}
        {moment(playlist.date).format("MMMM DD, YYYY")}
      </Accordion.Header>
      {user.isAuthenticated && (
        <div className="row">
          <div className="col-6">
            <PlaylistModal as={Button} playlist={playlist} variant="link">
              Edit Playlist
            </PlaylistModal>
          </div>
          <div className="col-6 text-end">
            <div
              className="btn btn-link"
              onClick={() =>
                fetchAPI(`/playlists/${playlist.show.id}/${playlist.id}/load/`, {
                  credentials: "include",
                })
              }
            >
              Load Playlist
            </div>
          </div>
        </div>
      )}
      <Accordion.Body>
        <div
          style={{ whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: playlist.description }}
        />
        {playlist.songs.length ? (
          <table className="show-detail-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Artist</th>
                <th className="d-none d-sm-table-cell">Album</th>
              </tr>
            </thead>
            <tbody>
              {playlist.songs.map((song, i) => (
                <tr
                  key={`${playlist.id}-${song.id}-${i}`}
                  className={
                    id === parseInt(song.id)
                      ? `table-active ${styles["table-active-current-song"]}`
                      : ""
                  }
                >
                  <td>{song.title}</td>
                  <td>{song.artist}</td>
                  <td className="d-none d-sm-table-cell">{song.album}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </Accordion.Body>
    </Accordion.Item>
  );
}

function Playlists({ showId, user }) {
  const [page, setPage] = useState(1);
  const { loading, data, refetch } = useQuery(playlistQuery, {
    variables: { id: showId },
  });
  const nowPlaying = useNowPlaying();

  useEffect(() => {
    if (data) {
      if (!data.playlistsByShowId.hasNext) setPage(0);
    }
  }, [data]);

  const handleMorePlaylists = () => {
    setPage((page) => {
      page += 1;
      refetch({ id: showId, page });
      return page;
    });
  };

  const { playlistsByShowId: playlists } = data || {
    playlistsByShowId: { edges: [] },
  };

  return (
    <>
      <Accordion>
        {playlists.edges.map((playlist, i) => (
          <Playlist
            key={i}
            playlist={playlist}
            user={user}
            id={
              parseInt(playlist.id) === nowPlaying.playlist.playlist_id &&
              nowPlaying.song.id
            }
          />
        ))}
      </Accordion>
      {page && !loading ? (
        <div className="text-center my-3">
          <button className="btn btn-primary" onClick={handleMorePlaylists}>
            Load More
          </button>
        </div>
      ) : loading ? (
        <Loader />
      ) : null}
    </>
  );
}

export default Playlists;

const playlistQuery = gql`
  query PlaylistsByShowId($id: ID!, $page: Int) {
    playlistsByShowId(showId: $id, page: $page) {
      hasNext
      edges: objects {
        __typename
        id
        date
        title
        description
        songs {
          __typename
          id
          title
          artist
          album
        }
        show {
          __typename
          id
          showName
        }
      }
    }
  }
`;
